import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { scannedCardsApi, ScannedBusinessCard, CardStatistics, DuplicateCandidate, isAuthenticated } from '../lib/api-scanned-cards';
import { useAuth } from '../context/AuthContext';
import { SalesKPIDashboard } from '../components/SalesKPIDashboard';
import { SalesPipelineBoard } from '../components/SalesPipelineBoard';
import {
  Search, Upload, FileText, Users, Building2, RefreshCw,
  MoreVertical, Trash2, Copy, Check, AlertCircle, Filter,
  Tag, Share2, Download, X, Sparkles, LogIn, ShieldAlert,
  LayoutGrid, List, BarChart3, Kanban, TrendingUp, Mail, Phone, Globe
} from 'lucide-react';

type ViewMode = 'list' | 'grid';
type TabType = 'cards' | 'dashboard' | 'pipeline';

function ScannedCardsContent() {
  const { t } = useTranslation();
  const auth = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('cards');
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  // Show loading while checking auth
  if (auth.loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto" />
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show auth UI if not authenticated
  if (!auth.isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="inline-flex p-4 bg-amber-100 rounded-full mb-6">
            <ShieldAlert className="w-12 h-12 text-amber-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Business Card Scanner</h2>
          <p className="text-slate-500 mb-6">AI-powered business card scanning & CRM integration</p>

          <div className="bg-slate-50 rounded-xl p-4 mb-6">
            <p className="text-sm text-slate-600">
              This feature requires authentication from the main Eureka app.
            </p>
            <p className="text-sm text-slate-600 mt-2">
              Please navigate from the main app or scan your QR code.
            </p>
          </div>

          {auth.error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm text-red-600">{auth.error}</p>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={() => {
                const returnUrl = encodeURIComponent(window.location.href);
                window.location.href = `https://eureka-factory.com/scanned-cards?redirect=${returnUrl}`;
              }}
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg shadow-blue-200"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <ScannedCardsPageContent
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        viewMode={viewMode}
        setViewMode={setViewMode}
      />
    </div>
  );
}

function ScannedCardsPageContent({
  activeTab,
  setActiveTab,
  viewMode,
  setViewMode
}: {
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
}) {
  const { t } = useTranslation();

  const [cards, setCards] = useState<ScannedBusinessCard[]>([]);
  const [stats, setStats] = useState<CardStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCard, setSelectedCard] = useState<ScannedBusinessCard | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [duplicates, setDuplicates] = useState<DuplicateCandidate[]>([]);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSync, setFilterSync] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadCards();
    loadStatistics();
  }, [page, filterStatus, filterSync]);

  async function loadCards() {
    try {
      setLoading(true);
      const result = await scannedCardsApi.list({
        query: searchQuery || undefined,
        page,
        limit: viewMode === 'grid' ? 50 : 20,
        status: filterStatus || undefined,
        sync: filterSync || undefined,
      });
      setCards(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotal(result.pagination.total);
    } catch (error: any) {
      console.error('Failed to load cards:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadStatistics() {
    try {
      const data = await scannedCardsApi.getStatistics();
      setStats(data);
    } catch (error: any) {
      console.error('Failed to load statistics:', error);
    }
  }

  async function handleUpload(files: FileList) {
    try {
      setUploading(true);
      const results = await scannedCardsApi.upload(Array.from(files), { autoSync: true });

      setTimeout(() => {
        loadCards();
        loadStatistics();
        setUploading(false);
        setShowUpload(false);
      }, 1000);
    } catch (error: any) {
      alert(`Upload failed: ${error.message}`);
      setUploading(false);
    }
  }

  async function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
    loadCards();
  }

  async function handleDelete(card: ScannedBusinessCard) {
    if (!confirm('Are you sure you want to delete this card?')) return;

    try {
      await scannedCardsApi.delete(card.id);
      loadCards();
      loadStatistics();
    } catch (error: any) {
      alert(`Failed to delete: ${error.message}`);
    }
  }

  async function handleFindDuplicates(card: ScannedBusinessCard) {
    try {
      const dupes = await scannedCardsApi.findDuplicates(card.id);
      setDuplicates(dupes);
      setSelectedCard(card);
      setShowDuplicates(true);
    } catch (error: any) {
      alert(`Failed to find duplicates: ${error.message}`);
    }
  }

  async function handleMerge(primaryId: string, mergeIds: string[]) {
    try {
      await scannedCardsApi.merge(primaryId, mergeIds);
      setShowDuplicates(false);
      loadCards();
      loadStatistics();
    } catch (error: any) {
      alert(`Failed to merge: ${error.message}`);
    }
  }

  async function handleSyncToCRM(cardId: string) {
    try {
      await scannedCardsApi.syncToCRM(cardId);
      loadCards();
    } catch (error: any) {
      alert(`Failed to sync: ${error.message}`);
    }
  }

  function getStatusBadge(status: string) {
    const styles: Record<string, string> = {
      PENDING: 'bg-slate-100 text-slate-700 border-slate-200',
      PROCESSING: 'bg-blue-100 text-blue-700 border-blue-200',
      COMPLETED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      FAILED: 'bg-red-100 text-red-700 border-red-200',
      NEEDS_REVIEW: 'bg-amber-100 text-amber-700 border-amber-200',
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.PENDING}`}>
        {status}
      </span>
    );
  }

  function getSyncBadge(status: string) {
    const styles: Record<string, string> = {
      UNSYNCED: 'bg-slate-100 text-slate-600 border-slate-200',
      SYNCING: 'bg-blue-100 text-blue-600 border-blue-200',
      SYNCED: 'bg-emerald-100 text-emerald-600 border-emerald-200',
      FAILED: 'bg-red-100 text-red-600 border-red-200',
      SKIPPED: 'bg-slate-100 text-slate-500 border-slate-200',
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.UNSYNCED}`}>
        {status === 'SYNCED' && <Check className="w-3 h-3 inline mr-1" />}
        {status}
      </span>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg shadow-blue-200">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Business Card Scanner</h1>
                <p className="text-sm text-slate-500">AI-powered OCR & CRM integration</p>
              </div>
            </div>
            <button
              onClick={() => setShowUpload(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 shadow-lg shadow-blue-200 transition-all duration-200 hover:scale-105"
            >
              <Upload className="w-5 h-5" />
              <span className="font-medium">Scan Cards</span>
            </button>
          </div>

          {/* Tabs */}
          <div className="mt-6 flex gap-2">
            <button
              onClick={() => setActiveTab('cards')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === 'cards'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Cards
            </button>
            <button
              onClick={() => setActiveTab('pipeline')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === 'pipeline'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <Kanban className="w-4 h-4 inline mr-2" />
              Pipeline
            </button>
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === 'dashboard'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Dashboard
            </button>
          </div>

          {/* Quick Stats */}
          {stats && (
            <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-100">
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-blue-700">{stats.totalCards}</span>
                  <FileText className="w-8 h-8 text-blue-400" />
                </div>
                <div className="text-sm text-blue-600 mt-1">Total Cards</div>
              </div>
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 border border-emerald-100">
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-emerald-700">{stats.completedCards}</span>
                  <Check className="w-8 h-8 text-emerald-400" />
                </div>
                <div className="text-sm text-emerald-600 mt-1">Processed</div>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-100">
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-purple-700">{stats.syncedCards}</span>
                  <Users className="w-8 h-8 text-purple-400" />
                </div>
                <div className="text-sm text-purple-600 mt-1">Synced to CRM</div>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-100">
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-orange-700">{stats.uniqueCompanies}</span>
                  <Building2 className="w-8 h-8 text-orange-400" />
                </div>
                <div className="text-sm text-orange-600 mt-1">Companies</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'cards' && (
          <CardsView
            cards={cards}
            loading={loading}
            viewMode={viewMode}
            setViewMode={setViewMode}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            handleSearch={handleSearch}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            filterSync={filterSync}
            setFilterSync={setFilterSync}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            total={total}
            getStatusBadge={getStatusBadge}
            getSyncBadge={getSyncBadge}
            handleDelete={handleDelete}
            handleFindDuplicates={handleFindDuplicates}
            handleSyncToCRM={handleSyncToCRM}
            setSelectedCard={setSelectedCard}
          />
        )}

        {activeTab === 'pipeline' && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Sales Pipeline</h2>
                <p className="text-sm text-slate-500">Drag cards between stages to update their status</p>
              </div>
            </div>
            <SalesPipelineBoard
              cards={cards}
              onCardClick={(card) => setSelectedCard(card)}
            />
          </div>
        )}

        {activeTab === 'dashboard' && (
          <SalesKPIDashboard stats={stats} />
        )}
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <UploadModal
          onClose={() => setShowUpload(false)}
          onUpload={handleUpload}
          uploading={uploading}
        />
      )}

      {/* Card Detail Modal */}
      {selectedCard && (
        <CardDetailModal
          card={selectedCard}
          onClose={() => setSelectedCard(null)}
          onSync={handleSyncToCRM}
          onDelete={handleDelete}
          onFindDuplicates={handleFindDuplicates}
        />
      )}

      {/* Duplicate Resolution Modal */}
      {showDuplicates && selectedCard && (
        <DuplicateResolutionModal
          card={selectedCard}
          duplicates={duplicates}
          onClose={() => setShowDuplicates(false)}
          onMerge={(mergeIds) => handleMerge(selectedCard.id, mergeIds)}
        />
      )}
    </div>
  );
}

// Cards View Component
function CardsView({
  cards,
  loading,
  viewMode,
  setViewMode,
  searchQuery,
  setSearchQuery,
  handleSearch,
  filterStatus,
  setFilterStatus,
  filterSync,
  setFilterSync,
  showFilters,
  setShowFilters,
  page,
  setPage,
  totalPages,
  total,
  getStatusBadge,
  getSyncBadge,
  handleDelete,
  handleFindDuplicates,
  handleSyncToCRM,
  setSelectedCard
}: {
  cards: ScannedBusinessCard[];
  loading: boolean;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  handleSearch: (e: React.FormEvent) => void;
  filterStatus: string;
  setFilterStatus: (status: string) => void;
  filterSync: string;
  setFilterSync: (sync: string) => void;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  page: number;
  setPage: (page: number) => void;
  totalPages: number;
  total: number;
  getStatusBadge: (status: string) => JSX.Element;
  getSyncBadge: (status: string) => JSX.Element;
  handleDelete: (card: ScannedBusinessCard) => void;
  handleFindDuplicates: (card: ScannedBusinessCard) => void;
  handleSyncToCRM: (cardId: string) => void;
  setSelectedCard: (card: ScannedBusinessCard) => void;
}) {
  return (
    <>
      {/* Search and Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, email, company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-3 rounded-xl border transition-all ${
              showFilters || filterStatus || filterSync
                ? 'bg-blue-50 border-blue-300 text-blue-700'
                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Filter className="w-5 h-5" />
            <span>Filters</span>
          </button>
          <div className="flex items-center gap-2 bg-slate-100 rounded-xl p-1">
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
            >
              <List className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
            >
              <LayoutGrid className="w-5 h-5" />
            </button>
          </div>
          <button
            type="submit"
            className="px-6 py-3 bg-slate-800 text-white rounded-xl hover:bg-slate-900 transition-colors"
          >
            Search
          </button>
        </form>

        {/* Expandable Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <select
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
              className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="PROCESSING">Processing</option>
              <option value="COMPLETED">Completed</option>
              <option value="NEEDS_REVIEW">Needs Review</option>
              <option value="FAILED">Failed</option>
            </select>
            <select
              value={filterSync}
              onChange={(e) => { setFilterSync(e.target.value); setPage(1); }}
              className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Sync States</option>
              <option value="UNSYNCED">Unsynced</option>
              <option value="SYNCED">Synced</option>
              <option value="FAILED">Sync Failed</option>
            </select>
          </div>
        )}
      </div>

      {/* Card List/Grid */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <RefreshCw className="w-12 h-12 text-blue-600 animate-spin" />
          <p className="mt-4 text-slate-600">Loading cards...</p>
        </div>
      ) : cards.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
          <div className="inline-flex p-6 bg-slate-100 rounded-full mb-6">
            <FileText className="w-16 h-16 text-slate-400" />
          </div>
          <h3 className="text-xl font-semibold text-slate-900 mb-2">No cards yet</h3>
          <p className="text-slate-500 mb-6">Scan your first business card to get started</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
          {cards.map((card) => (
            <div
              key={card.id}
              onClick={() => setSelectedCard(card)}
              className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-all cursor-pointer"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold shadow-md">
                  {card.firstName?.[0] || card.fullName?.[0] || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate">
                    {card.fullName || [card.firstName, card.lastName].filter(Boolean).join(' ') || '-'}
                  </p>
                  {card.title && (
                    <p className="text-sm text-slate-500 truncate">{card.title}</p>
                  )}
                </div>
              </div>
              {card.company && (
                <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  <span className="truncate">{card.company}</span>
                </div>
              )}
              <div className="flex gap-2 mb-3">
                {getStatusBadge(card.processingStatus)}
                {getSyncBadge(card.syncStatus)}
              </div>
              {card.email && (
                <div className="text-sm text-slate-600 truncate">
                  {card.email}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Contact</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Company</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">CRM Sync</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {cards.map((card) => (
                    <tr key={card.id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setSelectedCard(card)}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold shadow-md text-sm">
                            {card.firstName?.[0] || card.fullName?.[0] || '?'}
                          </div>
                          <div>
                            <div className="font-medium text-slate-900">
                              {card.fullName || [card.firstName, card.lastName].filter(Boolean).join(' ') || '-'}
                            </div>
                            <div className="text-sm text-slate-500">{card.title || ''}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {card.company && <Building2 className="w-4 h-4 text-slate-400" />}
                          <span className="text-slate-700">{card.company || '-'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-600">{card.email || '-'}</td>
                      <td className="px-6 py-4">{getStatusBadge(card.processingStatus)}</td>
                      <td className="px-6 py-4">{getSyncBadge(card.syncStatus)}</td>
                      <td className="px-6 py-4 text-slate-500 text-sm">
                        {new Date(card.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                          {card.processingStatus === 'COMPLETED' && card.syncStatus === 'UNSYNCED' && (
                            <button
                              onClick={() => handleSyncToCRM(card.id)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Sync to CRM"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(card)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-slate-500">
              Showing {(page - 1) * 20 + 1} to {Math.min(page * 20, total)} of {total} cards
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-slate-700">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// Card Detail Modal
function CardDetailModal({
  card,
  onClose,
  onSync,
  onDelete,
  onFindDuplicates
}: {
  card: ScannedBusinessCard;
  onClose: () => void;
  onSync: (cardId: string) => void;
  onDelete: (card: ScannedBusinessCard) => void;
  onFindDuplicates: (card: ScannedBusinessCard) => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900">Card Details</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {/* Card Image Preview */}
          {card.originalImageUrl && (
            <div className="mb-6 rounded-xl overflow-hidden border border-slate-200">
              <img src={card.originalImageUrl} alt="Business Card" className="w-full h-auto" />
            </div>
          )}

          {/* Contact Info */}
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                {card.firstName?.[0] || card.fullName?.[0] || '?'}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  {card.fullName || [card.firstName, card.lastName].filter(Boolean).join(' ') || '-'}
                </h3>
                {card.title && <p className="text-slate-500">{card.title}</p>}
              </div>
            </div>

            {card.company && (
              <div className="flex items-center gap-2 text-slate-700">
                <Building2 className="w-5 h-5 text-slate-400" />
                <span>{card.company}</span>
              </div>
            )}

            {card.email && (
              <div className="flex items-center gap-2 text-slate-700">
                <Mail className="w-5 h-5 text-slate-400" />
                <a href={`mailto:${card.email}`} className="text-blue-600 hover:underline">
                  {card.email}
                </a>
              </div>
            )}

            {card.phone && (
              <div className="flex items-center gap-2 text-slate-700">
                <Phone className="w-5 h-5 text-slate-400" />
                <a href={`tel:${card.phone}`} className="text-blue-600 hover:underline">
                  {card.phone}
                </a>
              </div>
            )}

            {card.website && (
              <div className="flex items-center gap-2 text-slate-700">
                <Globe className="w-5 h-5 text-slate-400" />
                <a href={card.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  {card.website}
                </a>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6 pt-6 border-t border-slate-200">
            {card.processingStatus === 'COMPLETED' && card.syncStatus === 'UNSYNCED' && (
              <button
                onClick={() => onSync(card.id)}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4 inline mr-2" />
                Sync to CRM
              </button>
            )}
            <button
              onClick={() => onFindDuplicates(card)}
              className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors"
            >
              <Copy className="w-4 h-4 inline mr-2" />
              Find Duplicates
            </button>
            <button
              onClick={() => onDelete(card)}
              className="px-4 py-2.5 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-colors"
            >
              <Trash2 className="w-4 h-4 inline mr-2" />
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Upload Modal Component
function UploadModal({ onClose, onUpload, uploading }: {
  onClose: () => void;
  onUpload: (files: FileList) => void;
  uploading: boolean;
}) {
  const [dragActive, setDragActive] = useState(false);

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files.length > 0) {
      onUpload(e.dataTransfer.files);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Upload className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Scan Business Cards</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          <div
            className={`relative border-2 border-dashed rounded-2xl p-12 text-center transition-all ${
              dragActive
                ? 'border-blue-500 bg-blue-50 scale-105'
                : 'border-slate-300 hover:border-slate-400'
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
          >
            <input
              id="file-upload"
              type="file"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              accept="image/*"
              multiple
              disabled={uploading}
              onChange={(e) => e.target.files && onUpload(e.target.files)}
            />
            <div className="pointer-events-none">
              <div className="inline-flex p-6 bg-blue-100 rounded-full mb-6">
                <Upload className="w-12 h-12 text-blue-600" />
              </div>
              <label htmlFor="file-upload" className="cursor-pointer">
                <p className="text-lg font-medium text-slate-900 mb-2">
                  {uploading ? 'Processing...' : 'Drop images here or click to upload'}
                </p>
                <p className="text-sm text-slate-500">Supports JPG, PNG, WEBP</p>
              </label>
            </div>
          </div>

          <div className="mt-6 p-4 bg-slate-50 rounded-xl">
            <p className="text-sm text-slate-600 space-y-1">
              <p>• AI extracts name, email, phone, company</p>
              <p>• Automatic duplicate detection</p>
              <p>• One-click sync to CRM</p>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Duplicate Resolution Modal Component
function DuplicateResolutionModal({
  card,
  duplicates,
  onClose,
  onMerge
}: {
  card: ScannedBusinessCard;
  duplicates: DuplicateCandidate[];
  onClose: () => void;
  onMerge: (mergeIds: string[]) => void;
}) {
  const [selectedMergeIds, setSelectedMergeIds] = useState<string[]>([]);

  function toggleMerge(id: string) {
    setSelectedMergeIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Copy className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Resolve Duplicates</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          <div className="mb-6 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
            <h3 className="font-semibold text-blue-900 mb-3">Current Card</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-blue-700">Name:</span>
                <p className="font-medium text-slate-900">{card.fullName || [card.firstName, card.lastName].filter(Boolean).join(' ') || '-'}</p>
              </div>
              <div>
                <span className="text-blue-700">Email:</span>
                <p className="font-medium text-slate-900">{card.email || 'N/A'}</p>
              </div>
              <div>
                <span className="text-blue-700">Company:</span>
                <p className="font-medium text-slate-900">{card.company || 'N/A'}</p>
              </div>
            </div>
          </div>

          <h3 className="font-semibold text-slate-900 mb-4">
            Matching Cards ({duplicates.length})
          </h3>

          {duplicates.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No duplicates found
            </div>
          ) : (
            <div className="space-y-3 mb-6">
              {duplicates.map((dup) => (
                <div
                  key={dup.id}
                  className={`relative rounded-xl border-2 p-4 cursor-pointer transition-all ${
                    selectedMergeIds.includes(dup.id)
                      ? 'border-purple-500 bg-purple-50 shadow-md'
                      : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                  }`}
                  onClick={() => toggleMerge(dup.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">
                        {dup.fullName || [dup.firstName, dup.lastName].filter(Boolean).join(' ') || '-'}
                      </p>
                      <div className="flex flex-wrap gap-3 mt-1 text-sm text-slate-600">
                        {dup.email && <span>{dup.email}</span>}
                        {dup.company && <span>• {dup.company}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm bg-purple-100 text-purple-700 px-3 py-1 rounded-full font-medium">
                        {Math.round(dup.matchScore * 100)}%
                      </span>
                      <input
                        type="checkbox"
                        checked={selectedMergeIds.includes(dup.id)}
                        onChange={() => toggleMerge(dup.id)}
                        className="h-5 w-5 text-purple-600 rounded"
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {dup.matchReason.map((reason, i) => (
                      <span key={i} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                        {reason}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button
              onClick={onClose}
              className="px-5 py-2.5 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => onMerge(selectedMergeIds)}
              disabled={selectedMergeIds.length === 0}
              className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-purple-200"
            >
              Merge Selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ScannedCardsPage() {
  return (
    <ScannedCardsContent />
  );
}
